<template>
  <!-- The head of every BF2026 page, on the campaign's navy: back button (none on
       FAST submitted outside Updates), the campaign banner (D), then the campaign
       tag and the gold heading on a band with rounded lower corners, centred under
       a banner.
       With a banner (D1–D5) the square artwork runs to the top edge, under the
       status bar, and the back button floats over it exactly as on the event page:
       absolute at the top-left, clearing the safe area with BackButton's own
       platform spacing. Without one the back button is in flow, and `flow-root`
       keeps the navy under the status bar: BackButton clears the safe area with a
       top MARGIN on Android and iPad, which would otherwise collapse through this
       band and leave the page's beige showing above it.
       `light` is a first-timer's submitted page (D1b–D4b): the "My 1st K" artwork
       (patch, stars and badge on a woven paper ground) behind banner AND band, with
       dark tag and heading. -->
  <header class="w-full bg-campaign-surface" data-test="bf-pledge-header">
    <!-- Light: one wrapper carries the artwork, the rounded corners and the shadow.
         The picture is 375x505 at 1x — the square banner, then paper for the band —
         drawn at the screen's width on a phone and at the content width (42rem, the
         `md:max-w-2xl` below) on anything wider, so it lines up with the square
         spacer that keeps the banner's room. The paper repeats down should the band
         run past it. -->
    <div
      :class="
        light &&
        'relative overflow-hidden rounded-b-[28px] bg-grey-5 bg-top bg-repeat-y bg-[length:100%_auto] md:bg-[length:42rem_auto] [box-shadow:var(--hogc-campaign-shadow)]'
      "
      :style="light ? artworkStyle : null"
      :data-test="light ? 'bf-pledge-first-art' : null"
    >
      <div
        :class="['relative flow-root', !light && 'bg-campaign-primary']"
        data-test="bf-pledge-header-band"
      >
        <div v-if="!banner && !back" class="pt-[env(safe-area-inset-top)]" />
        <div v-if="banner" class="relative md:max-w-2xl md:mx-auto">
          <img
            v-if="!light"
            :src="bannerUrl"
            alt=""
            class="block w-full aspect-square object-cover"
          />
          <div
            v-else
            role="img"
            aria-label="BF2026 My Privilege to Build patch, with My 1st K badge"
            class="w-full aspect-square"
          />
        </div>
        <BackButton
          v-if="back"
          :customBack="back"
          :variant="light ? 'default' : 'on-dark'"
          :class="banner ? 'absolute top-0 left-0 z-10 px-[16px]' : 'px-[16px] pb-[8px]'"
          data-test="bf-back"
        />
      </div>
      <div
        :class="[
          'relative px-[16px] pb-[26px]',
          banner ? 'pt-[18px]' : 'pt-[12px]',
          !light && 'rounded-b-[28px] bg-campaign-primary',
        ]"
      >
        <div
          :class="[
            'w-full md:max-w-2xl md:mx-auto flex flex-col',
            banner ? 'items-center text-center gap-[8px]' : 'items-start gap-[12px]',
          ]"
        >
          <DsTag variant="campaign-outline" :class="light && '!border-grey-80 !text-grey-80'"
            >BF2026 · MY PRIVILEGE TO BUILD</DsTag
          >
          <DsHeading
            tag="h1"
            :class="[
              'font-anton font-normal uppercase tracking-[0.01em]',
              light ? 'text-grey-80' : 'text-campaign-accent',
              small ? 'text-[32px] leading-[36px]' : 'text-[40px] leading-[44px]',
            ]"
          >
            <slot />
          </DsHeading>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { Heading as DsHeading, Tag as DsTag } from '@team-hogc/hogc-one-design-system';
import BackButton from '@/components/common/BackButton.vue';
import { BF_FIRST_ARTWORK, BF_PLEDGE_BANNER } from '@/constants/MyGiving/building-fund-pledge';

export default {
  name: 'PledgeHeader',

  components: { BackButton, DsHeading, DsTag },

  props: {
    // The back handler; leave it out for no back button.
    back: {
      type: Function,
      default: null,
    },
    // The campaign banner, with the tag and heading centred under it (D).
    banner: {
      type: Boolean,
      default: false,
    },
    // The smaller heading of the done page (D1–D3: 32/36 rather than 40/44).
    small: {
      type: Boolean,
      default: false,
    },
    // A first-timer's submitted page (D1b–D4b): light, with the "My 1st K" artwork.
    light: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      bannerUrl: BF_PLEDGE_BANNER,
      // An asset path, not a design token, so it goes on `style` the way `src`
      // does; every size, position and repeat is a class.
      artworkStyle: { backgroundImage: `url(${BF_FIRST_ARTWORK})` },
    };
  },
};
</script>
